import { MenuItem, Select, Stack } from '@mui/material';

import { Text, type api, isDefined, isTruthy } from 'common';

import { useSchedulingConfigsField } from './hooks/useSchedulingConfigsField';
import { useReactFlowViewContext } from '../../../../context/context';
import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate';

interface SchedulingConfigsReasonForVisitBlockProps {
  fieldNamePrefix: string;
}

enum ReasonForVisit {
  ScheduleAppointmentDefaultReasonForVisit = 'scheduleAppointmentDefaultReasonForVisit',
  ScheduleAppointmentCommentEnabled = 'scheduleAppointmentCommentEnabled',
  UseReasonForVisitFromPatientOrders = 'useReasonForVisitFromPatientOrders',
}

export function SchedulingConfigsReasonForVisitBlock(
  props: SchedulingConfigsReasonForVisitBlockProps
) {
  const { fieldNamePrefix } = props;
  const { value: schedulingConfigsValue, setValue } = useSchedulingConfigsField(fieldNamePrefix);
  const { isEditable } = useReactFlowViewContext();

  if (!isDefined(schedulingConfigsValue)) {
    return null;
  }

  const reasonForVisit = isTruthy(schedulingConfigsValue.scheduleAppointmentCommentEnabled)
    ? ReasonForVisit.ScheduleAppointmentCommentEnabled
    : isTruthy(schedulingConfigsValue.customConfigs?.useReasonForVisitFromPatientOrders)
      ? ReasonForVisit.UseReasonForVisitFromPatientOrders
      : ReasonForVisit.ScheduleAppointmentDefaultReasonForVisit;
  return (
    <Stack gap={1}>
      <Text variant="heading5">Reason for Visit settings</Text>
      <Select
        fullWidth
        value={reasonForVisit}
        onChange={(event) => {
          const reasonForVisitConfigs: Partial<
            api.FlowDefinition.FormBlockConfig['schedulingConfigs']
          > = {
            scheduleAppointmentCommentEnabled: false,
            customConfigs: {
              ...schedulingConfigsValue.customConfigs,
              useReasonForVisitFromPatientOrders: false,
              appointmentCommentModalTitle: undefined,
              appointmentCommentModalLabel: undefined,
              appointmentCommentModalPlaceholder: undefined,
            },
            scheduleAppointmentDefaultReasonForVisit: undefined,
          };

          const newValue = event.target.value;
          if (newValue === ReasonForVisit.ScheduleAppointmentDefaultReasonForVisit) {
            setValue({
              ...reasonForVisitConfigs,
              scheduleAppointmentDefaultReasonForVisit:
                schedulingConfigsValue.scheduleAppointmentDefaultReasonForVisit,
            });
          } else if (newValue === ReasonForVisit.ScheduleAppointmentCommentEnabled) {
            setValue({
              ...reasonForVisitConfigs,
              scheduleAppointmentCommentEnabled: true,
              customConfigs: {
                ...reasonForVisitConfigs.customConfigs,
                appointmentCommentModalTitle:
                  schedulingConfigsValue.customConfigs?.appointmentCommentModalTitle,
                appointmentCommentModalLabel:
                  schedulingConfigsValue.customConfigs?.appointmentCommentModalLabel,
                appointmentCommentModalPlaceholder:
                  schedulingConfigsValue.customConfigs?.appointmentCommentModalPlaceholder,
              },
            });
          } else {
            setValue({
              ...reasonForVisitConfigs,
              customConfigs: {
                ...reasonForVisitConfigs.customConfigs,
                useReasonForVisitFromPatientOrders: true,
              },
            });
          }
        }}
        disabled={!isEditable}
        variant="outlined"
      >
        <MenuItem
          key="Provide a default reason for visit"
          value={ReasonForVisit.ScheduleAppointmentDefaultReasonForVisit}
        >
          Provide a default reason for visit
        </MenuItem>
        <MenuItem
          key="Collect a reason for visit from the patient"
          value={ReasonForVisit.ScheduleAppointmentCommentEnabled}
        >
          Collect a reason for visit from the patient
        </MenuItem>
        <MenuItem
          key="Use the reason for visit from the patient orders"
          value={ReasonForVisit.UseReasonForVisitFromPatientOrders}
        >
          Use the reason for visit from the patient orders
        </MenuItem>
      </Select>
      {reasonForVisit === ReasonForVisit.ScheduleAppointmentDefaultReasonForVisit && (
        <BlockValueTemplateField
          fieldName={`${fieldNamePrefix}.scheduleAppointmentDefaultReasonForVisit`}
          label="Default reason for visit"
          helperText="Enter the default reason for visit (If empty, does nothing)"
        />
      )}
      {reasonForVisit === ReasonForVisit.ScheduleAppointmentCommentEnabled && (
        <>
          <BlockValueTemplateField
            fieldName={`${fieldNamePrefix}.customConfigs.appointmentCommentModalTitle`}
            label="Title"
            helperText="Reason for visit"
          />
          <BlockValueTemplateField
            fieldName={`${fieldNamePrefix}.customConfigs.appointmentCommentModalLabel`}
            label="Label"
            helperText="Please briefly describe the reason for your visit"
          />
          <BlockValueTemplateField
            fieldName={`${fieldNamePrefix}.customConfigs.appointmentCommentModalPlaceholder`}
            label="Placeholder"
            helperText="Example: knee pain"
          />
        </>
      )}
    </Stack>
  );
}
